import { distanceBetween, nearestIndex } from './route';

/**
 * Svänganvisningar ur OSRM:s steg, på svenska.
 *
 * OSRM beskriver varje manöver med en typ och en riktning. Här översätts
 * det till något man kan läsa i en blick under körning — riktningen först,
 * gatunamnet sist, eftersom det är riktningen man behöver veta.
 */
const DIRECTIONS = {
  left: 'vänster',
  right: 'höger',
  'slight left': 'vänster',
  'slight right': 'höger',
  'sharp left': 'skarpt vänster',
  'sharp right': 'skarpt höger',
};

function describe(step) {
  const maneuver = step.maneuver || {};
  const type = maneuver.type || '';
  const modifier = maneuver.modifier || '';
  const road = step.name || '';
  const onto = road ? ` in på ${road}` : '';

  switch (type) {
    case 'depart':
      return road ? `Kör ut på ${road}` : 'Kör iväg';
    case 'arrive':
      return 'Du är framme';
    case 'turn':
      if (modifier === 'straight') return road ? `Fortsätt på ${road}` : 'Fortsätt rakt fram';
      return `Sväng ${DIRECTIONS[modifier] || ''}${onto}`.replace('  ', ' ');
    case 'end of road':
      return `Sväng ${DIRECTIONS[modifier] || ''}${onto}`.replace('  ', ' ');
    case 'fork':
      return `Håll ${DIRECTIONS[modifier] || 'vänster'}${onto}`;
    case 'merge':
      return `Anslut${onto}`;
    case 'on ramp':
      return `Ta påfarten${onto}`;
    case 'off ramp':
      return `Ta avfarten${onto}`;
    case 'roundabout':
    case 'rotary':
      return maneuver.exit
        ? `I rondellen, ta avfart ${maneuver.exit}`
        : 'Kör in i rondellen';
    case 'exit roundabout':
    case 'exit rotary':
      return `Lämna rondellen${onto}`;
    case 'new name':
      return road ? `Fortsätt på ${road}` : 'Fortsätt';
    case 'continue':
      if (modifier && DIRECTIONS[modifier]) return `Fortsätt ${DIRECTIONS[modifier]}${onto}`;
      return road ? `Fortsätt på ${road}` : 'Fortsätt';
    default:
      return road ? `Fortsätt på ${road}` : 'Fortsätt';
  }
}

/** Meter från ruttens början till varje hörn. */
export function cumulativeDistances(route) {
  const totals = [0];
  for (let i = 1; i < route.length; i++) {
    totals[i] = totals[i - 1] + distanceBetween(route[i - 1], route[i]);
  }
  return totals;
}

/**
 * Manövrerna längs rutten, var och en med sitt avstånd från starten.
 * Räknas ut en gång per rutt så att varje positionsuppdatering bara
 * behöver jämföra tal.
 */
export function buildSteps(legs, route) {
  const totals = cumulativeDistances(route);
  const steps = [];

  for (const leg of legs || []) {
    for (const step of leg.steps || []) {
      const location = step.maneuver && step.maneuver.location;
      if (!location) continue;

      // OSRM ger [lng, lat]; rutten är [lat, lng].
      const { index } = nearestIndex(route, [location[1], location[0]]);
      steps.push({ at: totals[index], text: describe(step) });
    }
  }

  return { steps, totals, total: totals[totals.length - 1] || 0 };
}

/**
 * Nästa manöver utifrån var man befinner sig.
 * Manövrer man redan passerat hoppas över; några meters marginal gör att
 * instruktionen inte hoppar fram och tillbaka vid GPS-brus.
 */
export function nextInstruction(plan, position) {
  if (!plan || !plan.steps.length || !position) return null;

  const { index } = nearestIndex(plan.route, position);
  const travelled = plan.totals[index] || 0;

  const upcoming = plan.steps.find(step => step.at > travelled + 5);
  if (!upcoming) return null;

  return {
    text: upcoming.text,
    distance: Math.max(0, upcoming.at - travelled),
    travelled,
  };
}

/** "om 300 m" / "om 1,2 km" — eller "nu" när man är framme vid svängen. */
export function formatDistance(metres) {
  if (metres < 30) return 'nu';
  if (metres < 1000) return `om ${Math.round(metres / 10) * 10} m`;
  return `om ${(metres / 1000).toFixed(1).replace('.', ',')} km`;
}
